import { useNavigate } from "react-router-dom";
import { X, Zap, ArrowRight, MessageCircle, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

export function UpgradePrompt({ limitInfo, onClose }) {
  const navigate = useNavigate();
  const isStory = limitInfo?.type === "story_limit";
  const isTrialExpired = limitInfo?.type === "free_trial_expired";
  const tierName = limitInfo?.tier_name || "Free";
  const message = limitInfo?.message || "You've reached your daily limit.";

  const suggestions = isStory
    ? [
        { tier: "Omni+", price: "$9.99/mo", highlight: "5 stories per day" },
        { tier: "Omni Duo", price: "$19.99/mo", highlight: "Unlimited stories" },
      ]
    : [
        { tier: "Omni+", price: "$9.99/mo", highlight: "50 messages per day" },
        { tier: "Omni Duo", price: "$19.99/mo", highlight: "Unlimited messages" },
      ];

  // If user is already on Omni+, suggest add-on instead
  const isPlus = limitInfo?.tier === "omni_plus";
  const addonSuggestion = isPlus
    ? { name: "Extras Limited", price: "$5/mo", highlight: "Unlimited messages + 2 exclusive AI modes" }
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 animate-in fade-in duration-200" onClick={onClose}>
      <div
        data-testid="upgrade-prompt-modal"
        className="w-full sm:max-w-md bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-primary/10 to-primary/5 px-6 pt-6 pb-4">
          {!isTrialExpired && (
            <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full hover:bg-black/10 transition-colors">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mb-3">
            {isStory ? <BookOpen className="w-6 h-6 text-primary" /> : <MessageCircle className="w-6 h-6 text-primary" />}
          </div>
          <h3 className="text-lg font-bold text-foreground">{message}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isTrialExpired
              ? "Free tier is a one-time 30-day window and can't be renewed on this account — upgrade to keep chatting."
              : `Your ${tierName} plan resets tomorrow. Upgrade to keep the conversation going.`}
          </p>
        </div>

        {/* Suggestions */}
        <div className="px-6 py-4 space-y-2.5">
          {addonSuggestion ? (
            <button
              data-testid="upgrade-addon-btn"
              onClick={() => { navigate("/pricing"); onClose(); }}
              className="w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-all text-left group"
            >
              <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold">{addonSuggestion.name}</span>
                  <span className="text-xs font-semibold text-primary">{addonSuggestion.price}</span>
                </div>
                <p className="text-xs text-muted-foreground">{addonSuggestion.highlight}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            suggestions.map((s, i) => (
              <button
                key={s.tier}
                data-testid={`upgrade-tier-${i}`}
                onClick={() => { navigate("/pricing"); onClose(); }}
                className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 transition-all text-left group ${
                  i === 0 ? "border-primary/30 bg-primary/5 hover:bg-primary/10" : "border-border/50 hover:border-primary/20 hover:bg-muted/30"
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  i === 0 ? "bg-primary/15" : "bg-muted"
                }`}>
                  <Zap className={`w-5 h-5 ${i === 0 ? "text-primary" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">{s.tier}</span>
                    <span className={`text-xs font-semibold ${i === 0 ? "text-primary" : "text-muted-foreground"}`}>{s.price}</span>
                    {i === 0 && <span className="text-[9px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-bold">POPULAR</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">{s.highlight}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-2">
          <Button
            data-testid="upgrade-view-plans-btn"
            onClick={() => { navigate("/pricing"); onClose(); }}
            className="flex-1 btn-omni bg-primary text-primary-foreground"
          >
            View All Plans
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-muted-foreground"
            data-testid="upgrade-maybe-later-btn"
            style={isTrialExpired ? { display: "none" } : undefined}
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  );
}

export function UsageCounter({ messagesRemaining, messagesLimit, unlimited }) {
  if (unlimited || messagesRemaining < 0) return null;

  const percentage = messagesLimit > 0 ? (messagesRemaining / messagesLimit) * 100 : 100;
  const isLow = messagesRemaining <= 3;
  const isMedium = messagesRemaining <= Math.ceil(messagesLimit * 0.3);

  return (
    <div
      data-testid="usage-counter"
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all flex-shrink-0 ${
        isLow
          ? "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-200/50 dark:border-red-800/50"
          : isMedium
          ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/50"
          : "bg-muted/50 text-muted-foreground border border-border/30"
      }`}
    >
      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
        backgroundColor: isLow ? "rgb(239 68 68)" : isMedium ? "rgb(245 158 11)" : "rgb(156 163 175)"
      }} />
      <span>{messagesRemaining}/{messagesLimit}</span>
      <MessageCircle className="w-3 h-3" />
    </div>
  );
}
